import * as React from "react";
import { useSelector } from "react-redux";

import { ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { UiStateSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { FormEngineSelectors, FormEngineViews } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { FormModel } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";
import {
	DefaultFormModelMap,
	DefaultWidgetMap,
	EnablementEntry,
	FormModelMap,
	WidgetMap
} from "@com.mgmtp.a12.formengine/formengine-core/lib/view";
import { EnablementByButtonName } from "@com.mgmtp.a12.formengine/formengine-core/lib/view/internal/configuration/engine-configuration";
import {
	UaaExtendedUser,
	UaaOidcModifiedUser,
	UaaSelectors,
	UaaUser
} from "@com.mgmtp.a12.uaa/uaa-authentication-client";
import { UaaOidcUser } from "@com.mgmtp.a12.uaa/uaa-authentication-client/lib/internal/interfaces";
import { NEW_INSTANCE_IDENTIFIER } from "@com.mgmtp.a12.client/client-core/lib/core/application";

import { getSDManifest } from "../../../app/slices/stammdaten/selectors";
import { StammdatenManifest } from "../../../app/slices/stammdaten/types";
import { ActivityIdContext } from "../../../component/ActivityContext";
import { assertNonNullable } from "../../../utilities/assert";
import { getApplicationId } from "../../../utilities/applicationKeyUtils";
import { isAllowedRuntimeModule } from "../../../utilities/user";
import { RUNTIME_MODULE_CHECK_ROLES } from "../../../utilities/variables/modulfRuntimeAnnotations";
import { CustomButton } from "../../processFormEngine/components/customButton";
import { CustomEventButton } from "../../processFormEngine/components/customEventButton";
import { CustomInlineRepeat } from "../../processFormEngine/components/CustomInlineRepeat";
import { CustomTextCell } from "../../processFormEngine/components/CustomTextCell";
import { EqualizeRowLabelHeights } from "../../processFormEngine/components/EqualizeRowLabelHeights";

const widgetMap: WidgetMap = {
	...DefaultWidgetMap,
	Button: CustomButton
};

export function StammdatenFormEngine(props: FormEngineViews.FormEngineProps): React.ReactNode {
	const activityId = props.activityId;
	const model = useSelector(ActivitySelectors.activityPropById(activityId, a => a.descriptor.model));
	const instance = useSelector(ActivitySelectors.activityPropById(activityId, a => a.descriptor.instance));
	const sdId = model ? getApplicationId(model) : null;
	const manifest = useSelector(getSDManifest(sdId ?? undefined));
	const engineState = useSelector(FormEngineSelectors.engineState(activityId));
	const isReadonly = engineState && UiStateSelectors.readonly()(engineState);
	const formModel = engineState?.models.formModel;

	const user = useSelector(UaaSelectors.user);
	assertNonNullable(user);

	const byButtonNameEnablements = React.useMemo(
		() => getStammdatenButtonEnablements(isReadonly, manifest, formModel, user, instance),
		[isReadonly, manifest, formModel, user, instance]
	);

	const enablements = { byButtonName: byButtonNameEnablements };

	const formModelMap: FormModelMap = React.useMemo(
		() => ({
			...DefaultFormModelMap,
			EventButton: { component: CustomEventButton(manifest) },
			InlineRepeat: { component: CustomInlineRepeat(activityId, isReadonly ?? false) },
			TextCell: { component: CustomTextCell },
			Row: { component: EqualizeRowLabelHeights }
		}),
		[manifest, activityId, isReadonly]
	);

	if (!engineState) {
		return null;
	}

	return (
		<ActivityIdContext.Provider value={activityId}>
			<FormEngineViews.FormEngine
				{...props}
				widgetMap={widgetMap}
				formModelMap={formModelMap}
				enablements={enablements}
			/>
		</ActivityIdContext.Provider>
	);
}

function getStammdatenButtonEnablements(
	readonly?: boolean,
	manifest?: StammdatenManifest,
	formModel?: FormModel,
	user?: UaaUser | UaaOidcUser | UaaExtendedUser | UaaOidcModifiedUser,
	instance?: string
): EnablementByButtonName {
	assertNonNullable(user);

	const subheaderButtons = [
		...(formModel?.content?.subHeaderBox?.minorButtons?.button ?? []),
		...(formModel?.content?.subHeaderBox?.majorButtons?.button ?? [])
	];

	const unauthorisedSubheaderButtons = subheaderButtons.filter(it =>
		it.annotations?.find(
			e => e.name === RUNTIME_MODULE_CHECK_ROLES && !isAllowedRuntimeModule(user, e.value!, manifest)
		)
	);

	const buttonEnablements: Record<string, EnablementEntry> = {
		ShowChangelogButton: {
			hidden: !manifest?.runtimeModules?.some(it => it.name === "Changelog"),
			disabled: instance === NEW_INSTANCE_IDENTIFIER
		},
		...unauthorisedSubheaderButtons.reduce<Record<string, EnablementEntry>>((enablements, current) => {
			enablements[current.name] = { hidden: true };
			return enablements;
		}, {})
	};

	return {
		...buttonEnablements,
		save: { hidden: readonly },
		cancel: { hidden: false },
		loeschen: { hidden: readonly }
	};
}
